import type { ActivityOf } from '@wyn/content';
import { assertGame } from '../errors.ts';
import { createRng } from '../rng.ts';
import { clamp, type MinigameResult, type TurnEngineImpl } from './types.ts';

export interface Reagent {
  name: string;
  /** Potency, toxicity, volatility. */
  p: number;
  t: number;
  v: number;
}

export const REAGENTS: readonly Reagent[] = [
  { name: 'Mandrake root', p: 5, t: 2, v: 1 }, { name: 'Feverfew', p: 2, t: 0, v: 0 },
  { name: 'Nightshade', p: 6, t: 4, v: 1 }, { name: 'Brimstone', p: 3, t: 1, v: 3 },
  { name: 'Quicksilver', p: 4, t: 3, v: 3 }, { name: 'Moonwort', p: 3, t: 0, v: 1 },
  { name: 'Rock salt', p: 1, t: 0, v: -2 }, { name: 'Toad venom', p: 7, t: 5, v: 2 },
  { name: 'Wild honey', p: 1, t: -2, v: 0 }, { name: 'Wolfsbane', p: 4, t: 2, v: 0 },
  { name: 'Dragon’s blood', p: 6, t: 1, v: 4 }, { name: 'Yarrow', p: 2, t: -1, v: 1 },
];

export const ALCHEMY_MAX_REAGENTS = 5;
/**
 * Herbs vary from batch to batch: each reagent on the bench is up to one point of potency either side
 * of its usual. An alchemist can judge this many of the six at a recipe's own level, and one more for
 * every `ALCHEMY_JUDGE_PER` levels of practice beyond it; the rest are a gamble.
 */
export const ALCHEMY_JUDGED = 2;
export const ALCHEMY_JUDGE_PER = 5;
export const alchemyJudged = (level: number, activityLevel: number) =>
  clamp(ALCHEMY_JUDGED + Math.floor(Math.max(0, level - activityLevel) / ALCHEMY_JUDGE_PER), ALCHEMY_JUDGED, 6);
export const ALCHEMY_MAX_EACH = 2;
/** Past this, the fire has died and you brew in haste. */
export const ALCHEMY_TIME_MS = 60_000;

export interface AlchemyState {
  /** The reagents as they are in this batch. Private: the view shows the usual potency of any not judged. */
  offer: Reagent[];
  /** Which of the offer the alchemist can judge. Older sessions have no field: all judged. */
  known?: boolean[];
  /** The recipe: exact potency, toxicity at most, volatility at most. */
  potency: number;
  toxicity: number;
  volatility: number;
  brewed: { reagents: number[]; potency: number; toxicity: number; volatility: number; late: boolean } | null;
}

export type AlchemyMove = { type: 'brew'; reagents: number[] };

export interface AlchemyView {
  /** A reagent not `known` shows its usual potency; this batch's may be one either side. */
  offer: (Reagent & { known: boolean })[];
  potency: number;
  toxicity: number;
  volatility: number;
  maxReagents: number;
  maxEach: number;
  timeLimitMs: number;
  brewed: AlchemyState['brewed'];
}

const sums = (rs: Reagent[]) => ({ p: rs.reduce((n, r) => n + r.p, 0), t: rs.reduce((n, r) => n + r.t, 0), v: rs.reduce((n, r) => n + r.v, 0) });

function outcome(s: AlchemyState) {
  const b = s.brewed!;
  if (b.volatility > s.volatility) return { score: 0.08, title: 'The cauldron erupts!', erupted: true };
  if (b.reagents.length === 0) return { score: 0, title: 'An empty cauldron', erupted: false };
  const score = clamp(1 - Math.abs(s.potency - b.potency) * 0.12 - Math.max(0, b.toxicity - s.toxicity) * 0.2, 0, 1) * (b.late ? 0.6 : 1);
  return { score, title: score > 0.9 ? 'A flawless draught' : score > 0.5 ? 'A serviceable draught' : 'A murky brew', erupted: false };
}

/**
 * The Herbarium: hit a recipe's exact potency without the draught turning toxic or the cauldron
 * boiling over. The offered reagents and the recipe come from the server seed; the whole cauldron
 * goes in as one move. Ported from the Harrowmere prototype (the Apothecary).
 */
export const alchemy: TurnEngineImpl<ActivityOf<'alchemy'>, AlchemyState, AlchemyMove, AlchemyView> = {
  init(a, level, serverSeed) {
    const rng = createRng(serverSeed);
    const V = a.volatility;
    let offer: Reagent[] = [];
    let P = 0, T = 0;
    for (let tries = 0; tries < 500; tries++) {
      // The recipe is drawn from the batch as it is, so a perfect cauldron always exists.
      offer = rng.shuffle(REAGENTS).slice(0, 6).map((r) => ({ ...r, p: Math.max(0, r.p + rng.int(-1, 1)) }));
      const combo = Array.from({ length: a.reagents }, () => rng.pick(offer));
      const c = sums(combo);
      const fits = c.v <= V && c.p >= 7 && c.p <= 24 && combo.filter((r) => r === combo[0]).length <= ALCHEMY_MAX_EACH;
      // Keep the last attempt as a fallback so a recipe always exists.
      P = c.p;
      T = Math.max(0, c.t) + 1;
      if (fits) break;
    }
    const judged = new Set(rng.shuffle([0, 1, 2, 3, 4, 5]).slice(0, alchemyJudged(level, a.level)));
    return { offer, known: offer.map((_, i) => judged.has(i)), potency: P, toxicity: T, volatility: V, brewed: null };
  },

  move(_a, s, m, { elapsedMs }) {
    assertGame(!s.brewed, 'INVALID', 'The draught is already brewed.');
    assertGame(m.reagents.length <= ALCHEMY_MAX_REAGENTS, 'INVALID', `The cauldron holds ${ALCHEMY_MAX_REAGENTS} reagents.`);
    for (const i of m.reagents) assertGame(Number.isInteger(i) && i >= 0 && i < s.offer.length, 'INVALID', 'There is no such reagent on the bench.');
    for (const i of new Set(m.reagents)) assertGame(m.reagents.filter((j) => j === i).length <= ALCHEMY_MAX_EACH, 'INVALID', 'No more of that.');
    const c = sums(m.reagents.map((i) => s.offer[i]!));
    return { ...s, brewed: { reagents: [...m.reagents], potency: c.p, toxicity: c.t, volatility: c.v, late: elapsedMs > ALCHEMY_TIME_MS } };
  },

  view(_a, s) {
    return {
      offer: s.offer.map((r, i) => {
        const known = s.known?.[i] ?? true;
        return { ...r, p: known ? r.p : REAGENTS.find((u) => u.name === r.name)!.p, known };
      }),
      potency: s.potency,
      toxicity: s.toxicity,
      volatility: s.volatility,
      maxReagents: ALCHEMY_MAX_REAGENTS,
      maxEach: ALCHEMY_MAX_EACH,
      timeLimitMs: ALCHEMY_TIME_MS,
      brewed: s.brewed,
    };
  },

  result(a, s): MinigameResult | null {
    if (!s.brewed) return null;
    const b = s.brewed;
    const o = outcome(s);
    const made = !o.erupted && b.reagents.length > 0;
    return {
      score: o.score,
      title: o.title,
      text: o.erupted
        ? 'Green smoke fills the Herbarium and the herb-wife chases you out.'
        : made
          ? `Potency ${b.potency} for ${s.potency}, toxicity ${b.toxicity} (at most ${s.toxicity}).${b.late ? ' The fire died, so you brewed in haste.' : ''}`
          : 'You stare into an empty pot.',
      breakdown: [
        ['Potency', clamp(1 - Math.abs(s.potency - b.potency) / s.potency, 0, 1)],
        ['Purity', clamp(1 - Math.max(0, b.toxicity - s.toxicity) * 0.25, 0, 1)],
      ],
      yield: made ? { itemKey: a.product, quantity: 1, graded: true } : null,
      coin: 0,
      xpMult: 1,
      feats: [
        ...(made && !b.late && b.potency === s.potency && b.toxicity <= s.toxicity ? ['exact'] : []),
        ...(o.erupted ? ['erupted'] : []),
      ],
    };
  },
};
